// Section 17 steps 1-5, pure: for one resource, work out what the policy proposes, whether the
// shortage dialog must ask, and (given an answer) the final shares. Nothing here touches the
// draft, so the phase can pause after planning without having mutated anything (24.4).
import type {
  Person,
  PersonId,
  PhaseContext,
  PriorityGroupId,
  PromptAnswer,
  RationPolicyId,
  ShortagePrompt,
} from '../../core/types';
import { allocate } from './allocate';
import { rankGroups, rankHouseholds } from './groups';

export type Rationed = 'food' | 'water' | 'warmth';
export const RATIONED: readonly Rationed[] = ['food', 'water', 'warmth'];

export const EPS = 1e-9;

type Shares = Record<PersonId, number>;

export interface ResourcePlan {
  resource: Rationed;
  /** Stock covers demand and nothing is pinned: everyone fed in full (the fast path). */
  full: boolean;
  policy: RationPolicyId;
  /** Priority order in force (empty under equal shares). */
  order: PriorityGroupId[];
  floor: number;
  demand: Readonly<Record<PersonId, number>>;
  needed: number;
  /** Stock on hand for this resource (warmth: firewood plus cloth in Winter). */
  stock: number;
  /** Seed turned into food this season (eat freely, or the dialog's choice). */
  seedEaten: number;
  /** stock + seedEaten. */
  available: number;
  shortage: boolean;
  /** Ranked groups (empty when stock covers demand and nothing is pinned). */
  ranked: PersonId[][];
  /** Standing overrides that applied (Section 17 step 4). */
  standingPins: Shares;
  /** The policy's own result with no pins or edits (the announced rule). */
  policyShares: Shares;
  /** The proposal: policy plus standing pins, as shown in the dialog. */
  proposed: Shares;
  /** Final shares before household sharing. */
  final: Shares;
  /** Set when the dialog must ask and has no answer yet (the phase pauses). */
  prompt: ShortagePrompt | null;
  answered: boolean;
  /** The player changed the proposal in the dialog. */
  byHand: boolean;
  /** People given nothing by a standing pin or the dialog. */
  pinnedNone: PersonId[];
  /** People given less than the policy's own result by pins or edits. */
  deviated: PersonId[];
  /** The dialog's pin toggle: shares to keep as standing overrides. */
  answerPins: Shares;
  saveAsOrder: string | null;
}

interface Rule {
  policy: RationPolicyId;
  order: PriorityGroupId[];
  floor: number;
}

function ruleFor(ctx: PhaseContext, r: Rationed): Rule {
  const p = ctx.state.policies;
  const dedupe = (order: readonly PriorityGroupId[]): PriorityGroupId[] => [...new Set(order)];
  switch (r) {
    case 'food':
      return { policy: p.foodRation, order: dedupe(p.foodPriority), floor: p.foodFloor };
    case 'water': {
      const plan = ctx.cfg.rationing.waterPlans[p.waterRation];
      return { policy: plan.policy, order: dedupe(plan.order), floor: p.foodFloor };
    }
    case 'warmth': {
      const plan = ctx.cfg.rationing.warmthPlans[p.warmth];
      return { policy: plan.policy, order: dedupe(plan.order), floor: p.foodFloor };
    }
  }
}

/** Stock available for a resource. Section 7: cloth stands in for firewood in Winter. */
export function stockFor(ctx: PhaseContext, r: Rationed): number {
  const s = ctx.state.stocks;
  switch (r) {
    case 'food':
      return s.food.amount;
    case 'water':
      return s.water.amount;
    case 'warmth':
      return (
        s.wood.amount +
        (ctx.cfg.rationing.clothWarmthSeasons.includes(ctx.season.season)
          ? s.cloth.amount * ctx.cfg.needs.clothWarmth
          : 0)
      );
  }
}

/** Section 17 step 4: standing pins. Warmth pins are per household and Winter-only (A-05). */
function standingPins(ctx: PhaseContext, r: Rationed, people: readonly Person[]): Shares {
  const o = ctx.state.rationOverrides[r];
  const out: Shares = {};
  if (r === 'warmth') {
    if (!ctx.cfg.rationing.warmthPinSeasons.includes(ctx.season.season)) return out;
    for (const p of people) {
      const pin = o.households[p.householdId];
      if (pin !== undefined) out[p.id] = pin;
    }
    return out;
  }
  if (Object.keys(o.people).length === 0 && Object.keys(o.households).length === 0) return out;
  for (const p of people) {
    const pin = o.people[p.id] ?? o.households[p.householdId];
    if (pin !== undefined) out[p.id] = pin;
  }
  return out;
}

const unit = (x: number): number => Math.min(1, Math.max(0, Number.isFinite(x) ? x : 0));

/**
 * The dialog's shares as pins. Warmth is per household: each household answered for is pinned
 * as a unit at the demand-weighted mean of its members' answered shares.
 */
function asPins(
  r: Rationed,
  shares: Readonly<Record<PersonId, number>>,
  people: readonly Person[],
  demand: Readonly<Record<PersonId, number>>,
): Shares {
  const out: Shares = {};
  if (r !== 'warmth') {
    for (const p of people) {
      const s = shares[p.id];
      if (s !== undefined) out[p.id] = unit(s);
    }
    return out;
  }
  const byHousehold = new Map<string, { given: number; need: number; n: number }>();
  for (const p of people) {
    const s = shares[p.id];
    if (s === undefined) continue;
    const d = demand[p.id] ?? 0;
    const row = byHousehold.get(p.householdId) ?? { given: 0, need: 0, n: 0 };
    row.given += unit(s) * d;
    row.need += d;
    row.n += 1;
    byHousehold.set(p.householdId, row);
  }
  for (const p of people) {
    const row = byHousehold.get(p.householdId);
    if (row === undefined) continue;
    out[p.id] = row.need > 0 ? row.given / row.need : 1;
  }
  return out;
}

export function shortagePromptId(r: Rationed, turn: number): string {
  return `shortage:${r}:t${turn}`;
}

function fullShares(people: readonly Person[]): Shares {
  const out: Shares = {};
  for (const p of people) out[p.id] = 1;
  return out;
}

function covered(people: readonly Person[], shares: Shares): number {
  return people.filter((p) => (shares[p.id] ?? 1) >= 1 - EPS).length;
}

/** Plan one resource. `people` is the committed population in sorted id order. */
export function planResource(
  ctx: PhaseContext,
  r: Rationed,
  people: readonly Person[],
  demand: Readonly<Record<PersonId, number>>,
): ResourcePlan {
  const rule = ruleFor(ctx, r);
  let needed = 0;
  for (const p of people) needed += demand[p.id] ?? 0;
  const stock = stockFor(ctx, r);
  const pins = standingPins(ctx, r, people);
  const base: ResourcePlan = {
    resource: r,
    full: false,
    policy: rule.policy,
    order: rule.policy === 'equal' ? [] : rule.order,
    floor: rule.floor,
    demand,
    needed,
    stock,
    seedEaten: 0,
    available: stock,
    shortage: false,
    ranked: [],
    standingPins: pins,
    policyShares: {},
    proposed: {},
    final: {},
    prompt: null,
    answered: false,
    byHand: false,
    pinnedNone: [],
    deviated: [],
    answerPins: {},
    saveAsOrder: null,
  };
  const pinned = Object.keys(pins).length > 0;
  if (needed <= stock + EPS && !pinned) {
    const full = fullShares(people);
    return { ...base, full: true, policyShares: full, proposed: full, final: full };
  }

  // Section 7 and 18: seed. Eat freely takes it before anyone goes short; keep unless
  // starvation offers it in the dialog; always keep never touches it.
  const policies = ctx.state.policies;
  const gap = Math.max(0, needed - stock);
  const seed = r === 'food' ? Math.min(ctx.state.seedReserved, gap) : 0;
  const autoSeed = policies.seedReserve === 'eat-freely' ? seed : 0;
  const seedOffer = policies.seedReserve === 'keep-unless-starvation' ? seed : 0;
  const availableBase = stock + autoSeed;

  const ranked = (r === 'warmth' ? rankHouseholds : rankGroups)(ctx, base.order, people);
  const request = { policy: rule.policy, resource: r, ranked, demand, floor: rule.floor };
  const proposal = allocate(ctx, { ...request, available: availableBase, pinned: pins });
  const proposed = proposal.shares;
  const shortage = needed > availableBase + EPS;

  // Step 5: ask when someone the player did not pin falls below the floor, or always under
  // Manual (the engine hands its proposal to the player).
  const belowFloor = people.some(
    (p) =>
      pins[p.id] === undefined &&
      (demand[p.id] ?? 0) > 0 &&
      (proposed[p.id] ?? 1) < rule.floor - EPS,
  );
  const asks = shortage && policies.askOnShortage && (rule.policy === 'manual' || belowFloor);
  const id = shortagePromptId(r, ctx.season.turn);
  const found = asks ? ctx.answers[id] : undefined;
  const answer: Extract<PromptAnswer, { kind: 'shortage' }> | undefined =
    found?.kind === 'shortage' ? found : undefined;

  let prompt: ShortagePrompt | null = null;
  let final = proposed;
  let available = availableBase;
  let answerShares: Shares = {};
  let byHand = false;
  if (asks && answer === undefined && ctx.mode === 'live') {
    prompt = {
      kind: 'shortage',
      id,
      turn: ctx.season.turn,
      resource: r,
      covered: covered(people, proposed),
      population: people.length,
      needed,
      available: availableBase,
      shortfall: Math.max(0, needed - availableBase),
      floor: rule.floor,
      policy: rule.policy,
      order: proposal.order,
      proposed,
      demand: { ...demand },
      seedAvailable: seedOffer,
    };
  } else if (answer !== undefined) {
    const asked = Number.isFinite(answer.eatSeed) ? answer.eatSeed : 0;
    const eaten = Math.min(seedOffer, Math.max(0, asked));
    available = availableBase + eaten;
    answerShares = asPins(r, answer.shares, people, demand);
    final = allocate(ctx, {
      ...request,
      available,
      pinned: { ...pins, ...answerShares },
    }).shares;
    byHand = people.some((p) => {
      const given = answer.shares[p.id];
      return given !== undefined && Math.abs(unit(given) - (proposed[p.id] ?? 1)) > 1e-6;
    });
  }
  const seedEaten = available - stock;
  const allPins = { ...pins, ...answerShares };
  const policyShares =
    Object.keys(allPins).length === 0 && available === availableBase
      ? proposed
      : allocate(ctx, { ...request, available, pinned: {} }).shares;
  const pinnedNone: PersonId[] = [];
  const deviated: PersonId[] = [];
  for (const p of people) {
    const d = demand[p.id] ?? 0;
    const share = final[p.id] ?? 1;
    if (d > 0 && share <= EPS && allPins[p.id] === 0) pinnedNone.push(p.id);
    if (d > 0 && share < (policyShares[p.id] ?? 1) - 1e-6) deviated.push(p.id);
  }
  return {
    ...base,
    seedEaten,
    available,
    shortage,
    ranked,
    policyShares,
    proposed,
    final,
    prompt,
    answered: answer !== undefined,
    byHand,
    pinnedNone,
    deviated,
    answerPins: answer?.pins === undefined ? {} : asPins(r, answer.pins, people, demand),
    saveAsOrder:
      answer?.saveAsOrder === undefined ||
      answer.saveAsOrder === null ||
      answer.saveAsOrder.trim() === ''
        ? null
        : answer.saveAsOrder.trim(),
  };
}
